/**
 * Коды прав хаба (IC-2972). Дублируют серверный enum
 * `BeOpenHub.Internal.Permissions.HubPermissionCode` — держать синхронно с ним.
 *
 * Исторически коды объявлялись инлайн в каждом компоненте; гранулярные права
 * меню/стоплистов (фаза D) используются во многих местах, поэтому вынесены сюда
 * как единый источник правды. Значения существующих прав менять нельзя — они
 * персистентны (БД role_permissions, JWT permission_code).
 */
export const PermissionCode = {
  OrganizationView: 1000001,
  OrganizationEdit: 1000002,

  UserView: 1000011,
  UserEdit: 1000012,
  UserSecureEdit: 1000014,

  RoleView: 1000021,
  RoleEdit: 1000022,
  RoleDelete: 1000023,

  ProductView: 1000031,
  ProductEdit: 1000032,

  MenuView: 1000041,
  MenuEdit: 1000042,
  MenuDelete: 1000043,

  /** Ручная выгрузка меню и стоплистов. */
  MenuUpload: 1000044,

  /** Просмотр истории выгрузок меню и стоплистов. */
  MenuUploadHistoryView: 1000045,

  /** Настройка расписания автоматических выгрузок меню и стоплистов. */
  MenuUploadScheduleEdit: 1000046,

  /** Ручное изменение публикации меню (кнопка published и обратно). */
  MenuPublicationToggle: 1000047,

  /** Скачивание файлов результата конвертации меню из истории. */
  MenuConversionFileDownload: 1000048,

  PipeView: 1000051,
  PipeEdit: 1000052,
  PipeDelete: 1000053,

  ConfigurationView: 1000061,
  ConfigurationEdit: 1000062,

  ReportView: 1000071,

  SpecialInstructionView: 1000081,
  SpecialInstructionEdit: 1000082,

  /** Просмотр заказов (список, мастер-лист, история, детали, статистика). */
  OrderView: 1000091,

  /** Ручное изменение статуса заказа. */
  OrderEdit: 1000092,

  /** Просмотр событий организации. */
  EventView: 1000093,

  /** Подтверждение события (передача в обработку). */
  EventConfirm: 1000094,

  // Promo (1000095-97) принадлежит GetPos (GetPosPermissionCode.GetPosPromo*), из блока хаба удалено.
} as const;

/**
 * Коды прав GetPos (IC-2972 phase E round 2/3). Дублируют серверный enum
 * `BeOpen.GeCo.Pos.Permissions.GetPosPermissionCode` — держать синхронно с ним.
 * Отдельный блок 2000000-2999999: пользователям GetPos права выдаёт сам GetPos,
 * а не хаб, поэтому пересечений с `PermissionCode` здесь быть не может.
 */
export const GetPosPermissionCode = {
  GetPosOrganizationView: 2000001,
  GetPosOrganizationEdit: 2000002,

  GetPosProductView: 2000011,
  GetPosProductEdit: 2000012,
  GetPosProductDelete: 2000013,

  GetPosMenuView: 2000021,
  GetPosMenuEdit: 2000022,
  GetPosMenuDelete: 2000023,

  GetPosMenuUploadHistoryView: 2000031,
  GetPosMenuUploadScheduleEdit: 2000032,
  GetPosMenuConversionFileDownload: 2000033,

  GetPosPromoView: 2000061,
  GetPosPromoEdit: 2000062,
  GetPosPromoDelete: 2000063,

  GetPosRoleView: 2000071,
  GetPosRoleEdit: 2000072,
} as const;

/**
 * Коды прав микрофронтов. У каждого сервера свой блок, коды попадают в JWT (`permission_code`)
 * вместе с хабовыми, проверяют их сами микрофронты и их бэкенды. Хаб использует отсюда только
 * `*View` — для видимости ссылок на микрофронты в навигации.
 */

/** kiosk-server, настройки киоска (projectKey `kiosk`), блок 3000000. */
export const KioskPermissionCode = {
  KioskSettingsView: 3000001,
  KioskSettingsEdit: 3000002,
  KioskSchemaView: 3000011,
  KioskSchemaEdit: 3000012,
  KioskHiddenSettingsView: 3000031,
  /** Включает KioskHiddenSettingsView. */
  KioskHiddenSettingsEdit: 3000032,
  KioskNetworkEdit: 3000041,
  KioskNetworkDelete: 3000042,
  /** POS и киоски сети. */
  KioskPosEdit: 3000051,
  KioskPosDelete: 3000052,
  /** Синхронизация сети и поиск CRM-организаций. */
  KioskCrmSync: 3000061,
  /** Вебхуки (UI нет). */
  KioskWebhookView: 3000071,
  KioskWebhookEdit: 3000072,
  KioskWebhookDelete: 3000073,
} as const;

/** kiosk-server, Content Manager (projectKey `content`), блок 3100000. */
export const ContentManagerPermissionCode = {
  ContentManagerView: 3100001,
  ContentManagerEdit: 3100002,
  ContentManagerAttach: 3100003,
  /** Видео 100–350 МБ. */
  ContentManagerLargeVideoUpload: 3100011,
  /** Удаление контента (UI нет). */
  ContentManagerContentDelete: 3100021,
} as const;

/** Центр управления (projectKey `kiosk-cc`), блок 4000000. */
export const ControlCenterPermissionCode = {
  ControlCenterView: 4000001,
  ControlCenterRuleView: 4000011,
  ControlCenterRuleEdit: 4000012,
  ControlCenterPosRestart: 4000022,
  ControlCenterPosShowAll: 4000023,
  /** force-update, sync-terminals и т.п. (UI нет). */
  ControlCenterPosUpdate: 4000024,
  ControlCenterPosDelete: 4000025,
  /** Перезагрузка плагина, не кассы. */
  ControlCenterPluginReboot: 4000026,
  ControlCenterAgentView: 4000027,
  ControlCenterFilesFetch: 4000031,
  ControlCenterFilesUpload: 4000033,
  ControlCenterFilesDelete: 4000034,
  /** Target-version организации, CRM-организаций и POS, overrides правил. */
  ControlCenterTargetVersionEdit: 4000042,
  /** Чтение и правка ключа CRM. */
  ControlCenterCrmCredentialsEdit: 4000043,
  ControlCenterLicenseEdit: 4000044,
  ControlCenterExternalOrganizationSync: 4000045,
  ControlCenterExternalOrganizationEdit: 4000046,
  ControlCenterExternalOrganizationDetach: 4000047,
  ControlCenterOrganizationDelete: 4000048,
  ControlCenterNetworkRename: 4000051,
  ControlCenterNetworkMerge: 4000052,
  ControlCenterNetworksView: 4000054,
  ControlCenterFranchisesView: 4000055,
  ControlCenterFranchiseEdit: 4000056,
  ControlCenterFranchiseDelete: 4000057,
  /** Кнопка удаления терминала в ЦУ закомментирована. */
  ControlCenterTerminalDelete: 4000061,
  ControlCenterExtendedLicenseView: 4000071,
  /** Включает ControlCenterExtendedLicenseView. */
  ControlCenterExtendedLicenseEdit: 4000072,
} as const;

/** QR-меню (projectKey `qr-menu`), блок 5000000. */
export const QrMenuPermissionCode = {
  QrMenuView: 5000001,
  QrMenuEdit: 5000002,
  QrMenuCreate: 5000003,
  /** Удаление, список всех организаций (UI нет). */
  QrMenuDelete: 5000011,
} as const;
